import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { RefreshCwIcon, CheckCircle2Icon, XCircleIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
interface Question {
  id: number;
  text: string;
  correctAnswer: string;
  wrongAnswers: string[];
}
const ADULT_QUIZ_EN: Question[] = [{
  id: 1,
  text: 'What is the currency of Honduras?',
  correctAnswer: 'Lempira',
  wrongAnswers: ['Quetzal', 'Peso', 'Colón']
}, {
  id: 2,
  text: 'What is the largest city in Honduras by population?',
  correctAnswer: 'San Pedro Sula',
  wrongAnswers: ['Tegucigalpa', 'La Ceiba', 'Choloma']
}, {
  id: 3,
  text: 'Which Central American country does NOT border Honduras?',
  correctAnswer: 'Belize',
  wrongAnswers: ['Guatemala', 'El Salvador', 'Nicaragua']
}, {
  id: 4,
  text: "What is the name of Honduras' Caribbean island chain popular with tourists?",
  correctAnswer: 'Bay Islands (Islas de la Bahía)',
  wrongAnswers: ['Corn Islands', 'San Blas Islands', 'Galápagos Islands']
}, {
  id: 5,
  text: 'What traditional Honduran street food is made with a flour tortilla, beans, and cream?',
  correctAnswer: 'Baleada',
  wrongAnswers: ['Pupusa', 'Tamale', 'Enchilada']
}, {
  id: 6,
  text: 'What is the name of the vast rainforest biosphere reserve in eastern Honduras?',
  correctAnswer: 'Río Plátano (La Mosquitia)',
  wrongAnswers: ['Darién Gap', 'Petén Rainforest', 'Monteverde Cloud Forest']
}, {
  id: 7,
  text: 'Which Honduran soccer player is known for his career at D.C. United and Anderlecht?',
  correctAnswer: 'Andy Nájar',
  wrongAnswers: ['Carlo Costly', 'Wilson Palacios', 'David Suazo']
}, {
  id: 8,
  text: "What is Honduras' national flower?",
  correctAnswer: 'Orchid (Rhyncholaelia digbyana)',
  wrongAnswers: ['Hibiscus', 'Rose', 'Bird of Paradise']
}, {
  id: 9,
  text: 'What international investment body did Honduras rejoin in 2026 under President Asfura?',
  correctAnswer: 'ICSID',
  wrongAnswers: ['NATO', 'OPEC', 'WHO']
}, {
  id: 10,
  text: 'What is the approximate literacy rate in Honduras?',
  correctAnswer: '~89%',
  wrongAnswers: ['~65%', '~75%', '~95%']
}, {
  id: 11,
  text: 'What is the capital of Honduras?',
  correctAnswer: 'Tegucigalpa',
  wrongAnswers: ['San Pedro Sula', 'La Ceiba', 'Comayagua']
}, {
  id: 12,
  text: "Approximately what percentage of Honduras' GDP comes from remittances?",
  correctAnswer: '~27%',
  wrongAnswers: ['~10%', '~15%', '~40%']
}, {
  id: 13,
  text: 'Which American artist is known for making music that celebrates Honduras and Honduran culture?',
  correctAnswer: 'Don Ryvco',
  wrongAnswers: ['Pitbull', 'Bad Bunny', 'Romeo Santos']
}, {
  id: 14,
  text: 'Which ancient Mayan archaeological site is located in western Honduras?',
  correctAnswer: 'Copán',
  wrongAnswers: ['Tikal', 'Chichén Itzá', 'Palenque']
}, {
  id: 15,
  text: "What is Honduras' national bird?",
  correctAnswer: 'Scarlet Macaw',
  wrongAnswers: ['Quetzal', 'Toucan', 'Harpy Eagle']
}, {
  id: 16,
  text: 'Approximately how many people of Honduran descent live in the United States?',
  correctAnswer: '~1.8 million',
  wrongAnswers: ['~500,000', '~3.5 million', '~5 million']
}, {
  id: 17,
  text: 'What body of water borders Honduras to the north?',
  correctAnswer: 'Caribbean Sea',
  wrongAnswers: ['Pacific Ocean', 'Gulf of Mexico', 'Atlantic Ocean']
}, {
  id: 18,
  text: 'In what year did Honduras gain independence from Spain?',
  correctAnswer: '1821',
  wrongAnswers: ['1810', '1838', '1898']
}, {
  id: 19,
  text: "What are Honduras' top agricultural exports?",
  correctAnswer: 'Coffee, bananas, palm oil',
  wrongAnswers: ['Corn, rice, wheat', 'Sugarcane, tobacco, cotton', 'Avocados, mangoes, pineapples']
}, {
  id: 20,
  text: 'Who is the current President of Honduras (2026)?',
  correctAnswer: 'Nasry Asfura',
  wrongAnswers: ['Juan Orlando Hernández', 'Xiomara Castro', 'Manuel Zelaya']
}, {
  id: 21,
  text: 'Salvador Nasralla is famous in Honduras as a beloved TV host. What long-running sports show did he host?',
  correctAnswer: 'Cinco Deportivo',
  wrongAnswers: ['Deportes TN5', 'Gol Honduras', 'Fútbol de Primera']
}, {
  id: 22,
  text: 'Which Honduran-born fashion designer founded a celebrated menswear brand in New York known for refined tailoring and Latin flair?',
  correctAnswer: 'Carlos Campos',
  wrongAnswers: ['Oscar de la Renta', 'Narciso Rodriguez', 'Esteban Cortázar']
}, {
  id: 23,
  text: 'Shin Fujiyama went viral for his mission to build schools in Honduras. What is the name of his nonprofit organization?',
  correctAnswer: 'Students Helping Honduras',
  wrongAnswers: ['Schools for Honduras', 'Honduras Education Fund', 'Build Honduras']
}, {
  id: 24,
  text: 'Award-winning Honduran-American journalist Neida Sandoval is best known for co-anchoring which major Spanish-language morning show?',
  correctAnswer: 'Despierta América (Univision)',
  wrongAnswers: ['Hoy Día (Telemundo)', 'Al Rojo Vivo', 'Primer Impacto']
}, {
  id: 25,
  text: 'Which Honduran-American actress starred as Betty Suarez in "Ugly Betty" and co-founded the civic engagement organization HARNESS?',
  correctAnswer: 'America Ferrera',
  wrongAnswers: ['Salma Hayek', 'Gina Rodriguez', 'Stephanie Beatriz']
}, {
  id: 26,
  text: 'What traditional Honduran soup made with conch and coconut milk also became a famous song?',
  correctAnswer: 'Sopa de Caracol',
  wrongAnswers: ['Sopa de Mondongo', 'Sopa de Res', 'Sopa de Frijoles']
}, {
  id: 27,
  text: 'Which Honduran comedian and actor became famous in the U.S. for his stand-up specials on Comedy Central under the stage name "Carlos Mencia"?',
  correctAnswer: 'Ned Arnel Mencia',
  wrongAnswers: ['Carlos García', 'Roberto Mencia', 'Eduardo Mencia']
}, {
  id: 28,
  text: 'What is the name of the Garifuna-inspired fitness dance movement created by a Honduran-American that blends exercise with Honduran beats?',
  correctAnswer: 'Garifunarobics',
  wrongAnswers: ['Punta Fitness', 'Caribe Cardio', 'Ritmo Fit']
}, {
  id: 29,
  text: 'Which Honduran TikTok and food creator has gained nearly 700K followers sharing authentic Honduran recipes?',
  correctAnswer: 'Cocina Con Yesi',
  wrongAnswers: ['Sabores Catrachos', 'Honduras Kitchen', 'Chef Catracho']
}, {
  id: 30,
  text: 'In what U.S. city does the largest concentration of the Honduran diaspora live?',
  correctAnswer: 'Houston',
  wrongAnswers: ['Los Angeles', 'New York', 'Miami']
}];
const ADULT_QUIZ_ES: Question[] = [{
  id: 1,
  text: '¿Cuál es la moneda de Honduras?',
  correctAnswer: 'Lempira',
  wrongAnswers: ['Quetzal', 'Peso', 'Colón']
}, {
  id: 2,
  text: '¿Cuál es la ciudad más grande de Honduras por población?',
  correctAnswer: 'San Pedro Sula',
  wrongAnswers: ['Tegucigalpa', 'La Ceiba', 'Choloma']
}, {
  id: 3,
  text: '¿Qué país centroamericano NO limita con Honduras?',
  correctAnswer: 'Belice',
  wrongAnswers: ['Guatemala', 'El Salvador', 'Nicaragua']
}, {
  id: 4,
  text: '¿Cómo se llama la cadena de islas caribeñas de Honduras popular entre los turistas?',
  correctAnswer: 'Islas de la Bahía',
  wrongAnswers: ['Islas del Maíz', 'Islas San Blas', 'Islas Galápagos']
}, {
  id: 5,
  text: '¿Qué comida callejera hondureña se hace con tortilla de harina, frijoles y crema?',
  correctAnswer: 'Baleada',
  wrongAnswers: ['Pupusa', 'Tamal', 'Enchilada']
}, {
  id: 6,
  text: '¿Cómo se llama la vasta reserva de biosfera en el oriente de Honduras?',
  correctAnswer: 'Río Plátano (La Mosquitia)',
  wrongAnswers: ['Tapón del Darién', 'Selva del Petén', 'Bosque Nuboso Monteverde']
}, {
  id: 7,
  text: '¿Qué futbolista hondureño es conocido por su carrera en D.C. United y Anderlecht?',
  correctAnswer: 'Andy Nájar',
  wrongAnswers: ['Carlo Costly', 'Wilson Palacios', 'David Suazo']
}, {
  id: 8,
  text: '¿Cuál es la flor nacional de Honduras?',
  correctAnswer: 'Orquídea (Rhyncholaelia digbyana)',
  wrongAnswers: ['Hibisco', 'Rosa', 'Ave del Paraíso']
}, {
  id: 9,
  text: '¿A qué organismo internacional de inversión se reincorporó Honduras en 2026 bajo el Presidente Asfura?',
  correctAnswer: 'CIADI',
  wrongAnswers: ['OTAN', 'OPEP', 'OMS']
}, {
  id: 10,
  text: '¿Cuál es la tasa aproximada de alfabetización en Honduras?',
  correctAnswer: '~89%',
  wrongAnswers: ['~65%', '~75%', '~95%']
}, {
  id: 11,
  text: '¿Cuál es la capital de Honduras?',
  correctAnswer: 'Tegucigalpa',
  wrongAnswers: ['San Pedro Sula', 'La Ceiba', 'Comayagua']
}, {
  id: 12,
  text: '¿Aproximadamente qué porcentaje del PIB de Honduras proviene de remesas?',
  correctAnswer: '~27%',
  wrongAnswers: ['~10%', '~15%', '~40%']
}, {
  id: 13,
  text: '¿Qué artista americano es conocido por hacer música que celebra Honduras y la cultura hondureña?',
  correctAnswer: 'Don Ryvco',
  wrongAnswers: ['Pitbull', 'Bad Bunny', 'Romeo Santos']
}, {
  id: 14,
  text: '¿Qué sitio arqueológico maya se encuentra en el occidente de Honduras?',
  correctAnswer: 'Copán',
  wrongAnswers: ['Tikal', 'Chichén Itzá', 'Palenque']
}, {
  id: 15,
  text: '¿Cuál es el ave nacional de Honduras?',
  correctAnswer: 'Guacamaya Roja',
  wrongAnswers: ['Quetzal', 'Tucán', 'Águila Harpía']
}, {
  id: 16,
  text: '¿Aproximadamente cuántas personas de ascendencia hondureña viven en los Estados Unidos?',
  correctAnswer: '~1.8 millones',
  wrongAnswers: ['~500,000', '~3.5 millones', '~5 millones']
}, {
  id: 17,
  text: '¿Qué cuerpo de agua bordea Honduras al norte?',
  correctAnswer: 'Mar Caribe',
  wrongAnswers: ['Océano Pacífico', 'Golfo de México', 'Océano Atlántico']
}, {
  id: 18,
  text: '¿En qué año Honduras obtuvo su independencia de España?',
  correctAnswer: '1821',
  wrongAnswers: ['1810', '1838', '1898']
}, {
  id: 19,
  text: '¿Cuáles son las principales exportaciones agrícolas de Honduras?',
  correctAnswer: 'Café, bananos, aceite de palma',
  wrongAnswers: ['Maíz, arroz, trigo', 'Caña de azúcar, tabaco, algodón', 'Aguacates, mangos, piñas']
}, {
  id: 20,
  text: '¿Quién es el actual Presidente de Honduras (2026)?',
  correctAnswer: 'Nasry Asfura',
  wrongAnswers: ['Juan Orlando Hernández', 'Xiomara Castro', 'Manuel Zelaya']
}, {
  id: 21,
  text: 'Salvador Nasralla es famoso en Honduras como querido presentador de TV. ¿Qué programa deportivo de larga duración presentó?',
  correctAnswer: 'Cinco Deportivo',
  wrongAnswers: ['Deportes TN5', 'Gol Honduras', 'Fútbol de Primera']
}, {
  id: 22,
  text: '¿Qué diseñador de moda nacido en Honduras fundó una celebrada marca de ropa masculina en Nueva York conocida por su sastrería refinada y estilo latino?',
  correctAnswer: 'Carlos Campos',
  wrongAnswers: ['Oscar de la Renta', 'Narciso Rodriguez', 'Esteban Cortázar']
}, {
  id: 23,
  text: 'Shin Fujiyama se hizo viral por su misión de construir escuelas en Honduras. ¿Cómo se llama su organización sin fines de lucro?',
  correctAnswer: 'Students Helping Honduras',
  wrongAnswers: ['Schools for Honduras', 'Honduras Education Fund', 'Build Honduras']
}, {
  id: 24,
  text: '¿La galardonada periodista hondureño-americana Neida Sandoval es conocida por co-presentar qué importante programa matutino en español?',
  correctAnswer: 'Despierta América (Univision)',
  wrongAnswers: ['Hoy Día (Telemundo)', 'Al Rojo Vivo', 'Primer Impacto']
}, {
  id: 25,
  text: '¿Qué actriz hondureño-americana protagonizó a Betty Suarez en "Ugly Betty" y cofundó la organización de compromiso cívico HARNESS?',
  correctAnswer: 'America Ferrera',
  wrongAnswers: ['Salma Hayek', 'Gina Rodriguez', 'Stephanie Beatriz']
}, {
  id: 26,
  text: '¿Qué sopa tradicional hondureña hecha con caracol y leche de coco también se convirtió en una famosa canción?',
  correctAnswer: 'Sopa de Caracol',
  wrongAnswers: ['Sopa de Mondongo', 'Sopa de Res', 'Sopa de Frijoles']
}, {
  id: 27,
  text: '¿Qué comediante y actor hondureño se hizo famoso en EE.UU. por sus especiales de stand-up en Comedy Central bajo el nombre artístico "Carlos Mencia"?',
  correctAnswer: 'Ned Arnel Mencia',
  wrongAnswers: ['Carlos García', 'Roberto Mencia', 'Eduardo Mencia']
}, {
  id: 28,
  text: '¿Cómo se llama el movimiento de baile fitness inspirado en la cultura garífuna creado por un hondureño-americano que combina ejercicio con ritmos hondureños?',
  correctAnswer: 'Garifunarobics',
  wrongAnswers: ['Punta Fitness', 'Caribe Cardio', 'Ritmo Fit']
}, {
  id: 29,
  text: '¿Qué creadora hondureña de TikTok y comida ha ganado casi 700K seguidores compartiendo recetas auténticas hondureñas?',
  correctAnswer: 'Cocina Con Yesi',
  wrongAnswers: ['Sabores Catrachos', 'Honduras Kitchen', 'Chef Catracho']
}, {
  id: 30,
  text: '¿En qué ciudad de EE.UU. vive la mayor concentración de la diáspora hondureña?',
  correctAnswer: 'Houston',
  wrongAnswers: ['Los Ángeles', 'Nueva York', 'Miami']
}];
const KIDS_QUIZ_EN: Question[] = [{
  id: 1,
  text: 'What colors are on the Honduras flag? 🇭🇳',
  correctAnswer: 'Blue and white',
  wrongAnswers: ['Red and white', 'Green and yellow', 'Red and blue']
}, {
  id: 2,
  text: "What beautiful bird is Honduras' national bird? 🦜",
  correctAnswer: 'Scarlet Macaw',
  wrongAnswers: ['Penguin', 'Eagle', 'Owl']
}, {
  id: 3,
  text: "What ocean can you swim in on Honduras' north coast? 🌊",
  correctAnswer: 'Caribbean Sea',
  wrongAnswers: ['Arctic Ocean', 'Indian Ocean', 'Red Sea']
}, {
  id: 4,
  text: 'What yummy fruit is Honduras famous for growing? 🍌',
  correctAnswer: 'Bananas',
  wrongAnswers: ['Apples', 'Grapes', 'Strawberries']
}, {
  id: 5,
  text: 'What ancient people built the amazing ruins at Copán? 🏛️',
  correctAnswer: 'The Maya',
  wrongAnswers: ['The Romans', 'The Egyptians', 'The Vikings']
}, {
  id: 6,
  text: 'What language do most people speak in Honduras? 🗣️',
  correctAnswer: 'Spanish',
  wrongAnswers: ['English', 'French', 'Portuguese']
}, {
  id: 7,
  text: 'What is the capital city of Honduras? 🏙️',
  correctAnswer: 'Tegucigalpa',
  wrongAnswers: ['New York', 'Mexico City', 'Panama City']
}, {
  id: 8,
  text: 'What sport is the MOST popular in Honduras? ⚽',
  correctAnswer: 'Soccer (Fútbol)',
  wrongAnswers: ['Baseball', 'Basketball', 'Tennis']
}];
const KIDS_QUIZ_ES: Question[] = [{
  id: 1,
  text: '¿Qué colores tiene la bandera de Honduras? 🇭🇳',
  correctAnswer: 'Azul y blanco',
  wrongAnswers: ['Rojo y blanco', 'Verde y amarillo', 'Rojo y azul']
}, {
  id: 2,
  text: '¿Qué hermosa ave es el ave nacional de Honduras? 🦜',
  correctAnswer: 'Guacamaya Roja',
  wrongAnswers: ['Pingüino', 'Águila', 'Búho']
}, {
  id: 3,
  text: '¿En qué mar puedes nadar en la costa norte de Honduras? 🌊',
  correctAnswer: 'Mar Caribe',
  wrongAnswers: ['Océano Ártico', 'Océano Índico', 'Mar Rojo']
}, {
  id: 4,
  text: '¿Qué fruta deliciosa es famosa en Honduras? 🍌',
  correctAnswer: 'Bananos',
  wrongAnswers: ['Manzanas', 'Uvas', 'Fresas']
}, {
  id: 5,
  text: '¿Qué pueblo antiguo construyó las ruinas de Copán? 🏛️',
  correctAnswer: 'Los Mayas',
  wrongAnswers: ['Los Romanos', 'Los Egipcios', 'Los Vikingos']
}, {
  id: 6,
  text: '¿Qué idioma hablan la mayoría de las personas en Honduras? 🗣️',
  correctAnswer: 'Español',
  wrongAnswers: ['Inglés', 'Francés', 'Portugués']
}, {
  id: 7,
  text: '¿Cuál es la capital de Honduras? 🏙️',
  correctAnswer: 'Tegucigalpa',
  wrongAnswers: ['Nueva York', 'Ciudad de México', 'Ciudad de Panamá']
}, {
  id: 8,
  text: '¿Cuál es el deporte MÁS popular en Honduras? ⚽',
  correctAnswer: 'Fútbol',
  wrongAnswers: ['Béisbol', 'Baloncesto', 'Tenis']
}];
type GameState = 'intro' | 'playing' | 'finished';
type QuizMode = 'adult' | 'kids';
export function HondurasQuiz() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true
  });
  const {
    t,
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const [gameState, setGameState] = useState<GameState>('intro');
  const [quizMode, setQuizMode] = useState<QuizMode>('adult');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [shuffledOptions, setShuffledOptions] = useState<string[]>([]);
  const getActiveQuiz = () => {
    if (quizMode === 'adult') return isEs ? ADULT_QUIZ_ES : ADULT_QUIZ_EN;
    return isEs ? KIDS_QUIZ_ES : KIDS_QUIZ_EN;
  };
  const activeQuiz = getActiveQuiz();
  const totalQuestions = activeQuiz.length;
  useEffect(() => {
    if (gameState === 'playing') {
      const q = activeQuiz[currentQuestionIndex];
      const options = [q.correctAnswer, ...q.wrongAnswers];
      setShuffledOptions(options.sort(() => Math.random() - 0.5));
      setSelectedAnswer(null);
    }
  }, [currentQuestionIndex, gameState, quizMode, language]);
  const handleStart = (mode: QuizMode) => {
    setQuizMode(mode);
    setGameState('playing');
    setCurrentQuestionIndex(0);
    setScore(0);
    setSelectedAnswer(null);
  };
  const handleRestart = () => {
    setGameState('intro');
    setCurrentQuestionIndex(0);
    setScore(0);
    setSelectedAnswer(null);
  };
  const handleAnswer = (answer: string) => {
    if (selectedAnswer) return;
    setSelectedAnswer(answer);
    const isCorrect = answer === activeQuiz[currentQuestionIndex].correctAnswer;
    if (isCorrect) setScore(s => s + 1);
    setTimeout(() => {
      if (currentQuestionIndex < totalQuestions - 1) {
        setCurrentQuestionIndex(i => i + 1);
      } else {
        setGameState('finished');
      }
    }, 1500);
  };
  const currentQuestion = activeQuiz[currentQuestionIndex];
  const getEndMessage = () => {
    const pct = score / totalQuestions;
    if (quizMode === 'kids') {
      if (pct >= 0.9) return {
        text: t('quiz.kidsExpertTitle'),
        emoji: '🌟',
        color: 'text-yellow-500'
      };
      if (pct >= 0.7) return {
        text: t('quiz.kidsGreatTitle'),
        emoji: '🎉',
        color: 'text-haa-blue'
      };
      if (pct >= 0.5) return {
        text: t('quiz.kidsNotBadTitle'),
        emoji: '😊',
        color: 'text-haa-midblue'
      };
      return {
        text: t('quiz.kidsKeepLearningTitle'),
        emoji: '💪',
        color: 'text-gray-500'
      };
    }
    if (pct >= 0.9) return {
      text: t('quiz.expertTitle'),
      emoji: '🏆',
      color: 'text-yellow-500'
    };
    if (pct >= 0.7) return {
      text: t('quiz.greatTitle'),
      emoji: '🌟',
      color: 'text-haa-blue'
    };
    if (pct >= 0.5) return {
      text: t('quiz.notBadTitle'),
      emoji: '👍',
      color: 'text-haa-midblue'
    };
    return {
      text: t('quiz.keepLearningTitle'),
      emoji: '📚',
      color: 'text-gray-500'
    };
  };
  const endMessage = getEndMessage();
  return <section id="honduras-trivia" className="bg-[#F0F4FF] py-16 lg:py-24" aria-labelledby="quiz-heading" data-id="element-2150">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2151">
        {/* Section Header */}
        <div ref={titleRef} className="text-center mb-14" data-id="element-2152">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-2153">
            <span className="font-nunito text-haa-blue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-2154">
              {t('quiz.label')}
            </span>
            <h2 id="quiz-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-haa-navy tracking-wider mt-2" data-id="element-2155">
              {t('quiz.heading')}
            </h2>
            <div className="flex items-center justify-center gap-3 mt-4" data-id="element-2156">
              <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-2157" />
              <div className="w-3 h-3 rounded-full bg-haa-skyblue" data-id="element-2158" />
              <div className="w-12 h-1 bg-haa-midblue rounded-full" data-id="element-2159" />
            </div>
          </motion.div>
        </div>

        {/* Quiz Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={titleInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.2,
        duration: 0.5
      }} className="max-w-3xl mx-auto bg-white rounded-3xl shadow-xl overflow-hidden min-h-[440px] flex flex-col relative" data-id="element-2160">
          <AnimatePresence mode="wait" data-id="element-2161">
            {/* ── INTRO ── */}
            {gameState === 'intro' && <motion.div key="intro" initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 1.05
          }} transition={{
            duration: 0.4
          }} className="flex-1 flex flex-col items-center justify-center p-8 sm:p-12 text-center" data-id="element-2162">
                <span className="text-7xl sm:text-8xl mb-6 block drop-shadow-md" data-id="element-2163">
                  🇭🇳
                </span>
                <h3 className="font-bebas text-4xl sm:text-5xl text-haa-navy tracking-wider mb-3" data-id="element-2164">
                  {t('quiz.introTitle')}
                </h3>
                <p className="font-nunito text-gray-500 text-base sm:text-lg mb-10 max-w-md mx-auto" data-id="element-2165">
                  {t('quiz.introDescription')}
                </p>

                <div className="flex flex-col sm:flex-row items-center gap-4 w-full max-w-lg" data-id="element-2166">
                  <motion.button whileHover={{
                scale: 1.04,
                y: -3
              }} whileTap={{
                scale: 0.96
              }} onClick={() => handleStart('adult')} className="w-full sm:flex-1 flex flex-col items-center gap-2 bg-gradient-to-br from-haa-blue to-haa-navy text-white font-nunito font-bold text-base px-6 py-6 rounded-2xl shadow-lg hover:shadow-xl transition-all border border-white/10" data-id="element-2167">
                    <span className="text-3xl mb-1" data-id="element-2168">🦜</span>
                    <span className="font-bebas text-2xl tracking-wider" data-id="element-2169">
                      {t('quiz.adultQuiz')}
                    </span>
                    <span className="font-nunito text-white/60 text-sm font-normal" data-id="element-2170">
                      {t('quiz.adultCount')}
                    </span>
                  </motion.button>

                  <motion.button whileHover={{
                scale: 1.04,
                y: -3
              }} whileTap={{
                scale: 0.96
              }} onClick={() => handleStart('kids')} className="w-full sm:flex-1 flex flex-col items-center gap-2 bg-gradient-to-br from-amber-400 to-orange-500 text-white font-nunito font-bold text-base px-6 py-6 rounded-2xl shadow-lg hover:shadow-xl transition-all border border-white/10" data-id="element-2171">
                    <span className="text-3xl mb-1" data-id="element-2172">🌟</span>
                    <span className="font-bebas text-2xl tracking-wider" data-id="element-2173">
                      {t('quiz.kidsQuiz')}
                    </span>
                    <span className="font-nunito text-white/60 text-sm font-normal" data-id="element-2174">
                      {t('quiz.kidsCount')}
                    </span>
                  </motion.button>
                </div>
              </motion.div>}

            {/* ── PLAYING ── */}
            {gameState === 'playing' && <motion.div key={`playing-${currentQuestionIndex}`} initial={{
            opacity: 0,
            x: 30
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -30
          }} transition={{
            duration: 0.35
          }} className="flex-1 flex flex-col p-6 sm:p-10" data-id="element-2175">
                <div className="mb-6" data-id="element-2176">
                  <div className="flex justify-between items-center mb-3" data-id="element-2177">
                    <div className="flex items-center gap-2" data-id="element-2178">
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-nunito font-bold uppercase tracking-wider ${quizMode === 'kids' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-haa-blue'}`} data-id="element-2179">
                        {quizMode === 'kids' ? `🌟 ${t('quiz.kids')}` : `🦜 ${t('quiz.adult')}`}
                      </span>
                      <span className="font-nunito font-bold text-haa-navy text-sm" data-id="element-2180">
                        {currentQuestionIndex + 1} / {totalQuestions}
                      </span>
                    </div>
                    <span className="font-nunito font-bold text-gray-400 text-sm" data-id="element-2181">
                      {t('quiz.score')}:{' '}
                      <span className="text-haa-navy" data-id="element-2182">{score}</span>
                    </span>
                  </div>
                  <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-2183">
                    <motion.div className={`h-full rounded-full ${quizMode === 'kids' ? 'bg-gradient-to-r from-amber-400 to-orange-500' : 'bg-gradient-to-r from-haa-blue to-haa-skyblue'}`} initial={{
                  width: `${currentQuestionIndex / totalQuestions * 100}%`
                }} animate={{
                  width: `${(currentQuestionIndex + 1) / totalQuestions * 100}%`
                }} transition={{
                  duration: 0.5,
                  ease: 'easeInOut'
                }} data-id="element-2184" />
                  </div>
                </div>

                <h3 className={`font-bebas tracking-wide leading-tight mb-8 ${quizMode === 'kids' ? 'text-2xl sm:text-3xl text-haa-navy' : 'text-3xl sm:text-4xl text-haa-navy'}`} data-id="element-2185">
                  {currentQuestion.text}
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-auto" data-id="element-2186">
                  {shuffledOptions.map((option, idx) => {
                const isSelected = selectedAnswer === option;
                const isCorrect = option === currentQuestion.correctAnswer;
                const hasAnswered = selectedAnswer !== null;
                let buttonStyle = quizMode === 'kids' ? 'bg-amber-50 border-amber-200 text-gray-700 hover:border-amber-400 hover:bg-amber-100' : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-haa-blue hover:bg-blue-50';
                let icon = null;
                if (hasAnswered) {
                  if (isCorrect) {
                    buttonStyle = 'bg-green-50 border-green-500 text-green-800 shadow-sm';
                    icon = <CheckCircle2Icon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-2187" />;
                  } else if (isSelected) {
                    buttonStyle = 'bg-red-50 border-red-500 text-red-800 shadow-sm';
                    icon = <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-2188" />;
                  } else {
                    buttonStyle = 'bg-gray-50 border-gray-200 text-gray-400 opacity-60';
                  }
                }
                return <motion.button key={option} initial={{
                  opacity: 0,
                  y: 12
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: idx * 0.08,
                  duration: 0.3
                }} disabled={hasAnswered} onClick={() => handleAnswer(option)} className={`relative flex items-center justify-between p-4 sm:p-5 rounded-xl border-2 font-nunito font-bold text-left transition-all duration-300 ${buttonStyle} ${!hasAnswered ? 'hover:-translate-y-1 hover:shadow-md cursor-pointer' : ''} ${quizMode === 'kids' ? 'text-base' : 'text-sm sm:text-base'}`} data-id="element-2189">
                        <span className="pr-3" data-id="element-2190">{option}</span>
                        {icon}
                      </motion.button>;
              })}
                </div>
              </motion.div>}

            {/* ── FINISHED ── */}
            {gameState === 'finished' && <motion.div key="finished" initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5,
            type: 'spring'
          }} className="flex-1 flex flex-col items-center justify-center p-8 sm:p-12 text-center" data-id="element-2191">
                <motion.div initial={{
              scale: 0,
              rotate: -180
            }} animate={{
              scale: 1,
              rotate: 0
            }} transition={{
              delay: 0.2,
              type: 'spring',
              stiffness: 200
            }} className="text-7xl sm:text-8xl mb-4" data-id="element-2192">
                  {endMessage.emoji}
                </motion.div>

                <h3 className={`font-bebas text-5xl sm:text-6xl tracking-wider mb-2 ${endMessage.color}`} data-id="element-2193">
                  {endMessage.text}
                </h3>

                <p className="font-nunito text-gray-600 text-xl mb-2" data-id="element-2194">
                  {t('quiz.youScored')}{' '}
                  <span className="font-bold text-haa-navy" data-id="element-2195">{score}</span>{' '}
                  {t('quiz.outOf')} {totalQuestions}!
                </p>

                <p className="font-nunito text-gray-400 text-sm mb-8" data-id="element-2196">
                  {quizMode === 'kids' ? `🌟 ${t('quiz.kidsQuiz')}` : `🦜 ${t('quiz.adultQuiz')}`}
                </p>

                <div className="flex flex-col sm:flex-row items-center gap-3" data-id="element-2197">
                  <motion.button whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} onClick={() => handleStart(quizMode)} className={`inline-flex items-center gap-2 font-nunito font-bold text-base px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all text-white ${quizMode === 'kids' ? 'bg-gradient-to-r from-amber-400 to-orange-500' : 'bg-gradient-to-r from-haa-blue to-haa-midblue'}`} data-id="element-2198">
                    <RefreshCwIcon className="w-5 h-5" data-id="element-2199" />
                    {t('quiz.playAgain')}
                  </motion.button>

                  <motion.button whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} onClick={handleRestart} className="inline-flex items-center gap-2 bg-white border-2 border-gray-200 text-haa-navy font-nunito font-bold text-base px-8 py-4 rounded-full shadow hover:shadow-md hover:border-haa-blue transition-all" data-id="element-2200">
                    {t('quiz.switchQuiz')}
                  </motion.button>
                </div>
              </motion.div>}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>;
}